import { useState, useRef } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Smile, MessageCircle, X } from 'lucide-react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { cn } from '@/lib/utils';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  showPreview?: boolean;
  previewTitle?: string;
}

export function MessageInput({
  value,
  onChange,
  label = 'Message',
  placeholder = 'Enter your message...',
  maxLength = 500,
  showPreview = true,
  previewTitle = 'Preview',
}: MessageInputProps) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const textarea = textareaRef.current;
    
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.slice(0, start) + emoji + value.slice(end);
      
      if (newValue.length <= maxLength) {
        onChange(newValue);
        // Set cursor position after emoji
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
          textarea.focus();
        }, 0);
      }
    } else {
      if ((value + emoji).length <= maxLength) {
        onChange(value + emoji);
      }
    }
  };

  const handleClear = () => {
    onChange('');
    textareaRef.current?.focus();
  };

  const characterCount = value.length;
  const isNearLimit = characterCount > maxLength * 0.8;
  const isAtLimit = characterCount >= maxLength;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {label && <Label>{label}</Label>}
        <span
          className={cn(
            'text-xs',
            isAtLimit && 'text-destructive font-medium',
            isNearLimit && !isAtLimit && 'text-secondary-foreground',
            !isNearLimit && 'text-muted-foreground'
          )}
        >
          {characterCount}/{maxLength}
        </span>
      </div>

      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            if (e.target.value.length <= maxLength) {
              onChange(e.target.value);
            }
          }}
          placeholder={placeholder}
          className="min-h-[100px] resize-none pr-20"
          maxLength={maxLength}
        />

        {/* Action buttons */}
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1.5 hover:bg-accent rounded-md transition-colors"
              title="Clear message"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          
          <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                title="Add emoji"
              >
                <Smile className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              className="w-auto p-0 border-0" 
              align="end"
              side="top"
            >
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                theme={Theme.AUTO}
                width={320}
                height={400}
                searchPlaceholder="Search emoji..."
                previewConfig={{ showPreview: false }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Message Preview */}
      {showPreview && value && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground flex items-center gap-1">
            <MessageCircle className="h-3 w-3" />
            {previewTitle}
          </Label>
          <div className="bg-primary/10 dark:bg-primary/20 rounded-lg p-3 relative max-w-[280px]">
            <p className="text-sm text-foreground whitespace-pre-wrap break-words">
              {value}
            </p>
            <div className="flex justify-end mt-1">
              <span className="text-[10px] text-muted-foreground">
                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            {/* WhatsApp-style tail */}
            <div className="absolute -right-2 top-0 w-0 h-0 border-l-8 border-l-[#DCF8C6] dark:border-l-[#025C4C] border-t-8 border-t-transparent" />
          </div>
        </div>
      )}
    </div>
  );
}
